import * as React from "react";
import { cn } from "./cn.js";

export type AlertTone = "info" | "warning" | "danger" | "success" | "neutral";

const TONES: Record<AlertTone, string> = {
  info: "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-foreground",
  warning: "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 text-foreground",
  danger: "border-destructive/50 bg-destructive/10 text-foreground",
  success: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-foreground",
  neutral: "border-border bg-muted text-foreground",
};

const ICON_TONES: Record<AlertTone, string> = {
  info: "text-[hsl(var(--info))]",
  warning: "text-[hsl(var(--warning))]",
  danger: "text-destructive",
  success: "text-[hsl(var(--success))]",
  neutral: "text-muted-foreground",
};

/** Screen-reader prefix so the severity is never conveyed by colour alone. */
const TONE_PREFIX: Record<AlertTone, string> = {
  info: "Note",
  warning: "Warning",
  danger: "Critical",
  success: "Success",
  neutral: "Note",
};

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: AlertTone;
  title: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  /** Raise the visual weight — used for the escalating certificate banners. */
  emphasis?: boolean;
}

export function Alert({
  className,
  tone = "info",
  title,
  icon,
  actions,
  emphasis = false,
  children,
  ...props
}: AlertProps) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn(
        "flex flex-wrap items-start gap-3 rounded-lg border p-4",
        TONES[tone],
        emphasis && "border-2 shadow-sm",
        className,
      )}
      {...props}
    >
      {icon ? (
        <span aria-hidden="true" className={cn("mt-0.5 shrink-0", ICON_TONES[tone])}>
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1 space-y-1">
        <p className={cn("text-sm font-semibold", emphasis && "text-base")}>
          <span className="sr-only">{TONE_PREFIX[tone]}: </span>
          {title}
        </p>
        {children ? <div className="text-sm text-muted-foreground">{children}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
